import { OPTIMIZATION_TYPE } from '@bringg/types';
import type { EtaResponseRouteItem, RunChangedParams } from '@bringg-frontend/eta-calculator';
import type { RoutesPlannerRun } from '../../types';
import type RoutesPlannerStore from '../../stores/routes-planner-store';
import { etaRoutesConverter } from './eta-routes-converter';
import { dateUtils } from '../../utils/date-utils';
import { MINUTE_IN_MS, SECOND_IN_MS } from '../../utils/bringgTypesTransformers';

export type RouteNode = { route: RunChangedParams; nextRoutes: { [runId: number]: RouteNode }; parent: RouteNode };
export type RouteGroup = { routes: RunChangedParams[]; minStartTimeMs: number; totalRunIds: number[] };

function getRouteNodes(changedRoute: RunChangedParams, routesPlannerStore: RoutesPlannerStore): RouteNode[] {
	const routesWithConsecutive = getAllPossibleRoutes(routesPlannerStore, [changedRoute]);
	const group = getGroupData(changedRoute, routesWithConsecutive, routesPlannerStore);

	return Object.values(group.byRunId).filter((routeNode: RouteNode) => routeNode.route.runOrderedTasks?.length > 0);
}

function getGroupedByResource(changedRoutes: RunChangedParams[], routesPlannerStore: RoutesPlannerStore): RouteGroup[] {
	const routesWithConsecutive = getAllPossibleRoutes(routesPlannerStore, changedRoutes);
	const { initialUnloadingTimeMinutes } = routesPlannerStore.configuration;
	const initialUnloadingTimeMs = (initialUnloadingTimeMinutes || 0) * MINUTE_IN_MS;
	const results: RouteGroup[] = [];
	const changedRoutesSorted = changedRoutes
		.slice()
		.sort((routeA, routeB) => routeA.runStartTime - routeB.runStartTime);

	for (const changedRoute of changedRoutesSorted) {
		if (existsInPreviousResults(results, changedRoute)) {
			continue;
		}
		const { nextRoutesRoot, previousRoutesRoot } = getGroupData(
			changedRoute,
			routesWithConsecutive,
			routesPlannerStore
		);

		const result = { routes: [], minStartTimeMs: undefined, totalRunIds: [] };

		traverseRoutesBfs(nextRoutesRoot, routeNode => {
			routeNode.route.routeIndex = routeNode.parent ? routeNode.parent.route.routeIndex + 1 : 0;

			if (routeNode.route.runOrderedTasks?.length > 0) {
				result.routes.push(routeNode.route);
			}

			if (routeNode.route.runId !== routeNode.route.routeIndex) {
				result.totalRunIds.push(routeNode.route.runId);
			}
			return false;
		});

		if (result.routes.length > 0) {
			traverseRoutesBfs(previousRoutesRoot, routeNode => {
				if (previousRoutesRoot.route.runId === routeNode.route.runId) {
					return false;
				}

				if (routeNode.route.runOrderedTasks?.length > 0) {
					const runEndTimeMs = routeNode.route.runEndTime * SECOND_IN_MS + initialUnloadingTimeMs;

					if (!result.minStartTimeMs || result.minStartTimeMs < runEndTimeMs) {
						result.minStartTimeMs = runEndTimeMs;
					}
				}

				if (routeNode.route.runId !== routeNode.route.routeIndex) {
					result.totalRunIds.push(routeNode.route.runId);
				}

				return false;
			});

			results.push(result);
		}
	}

	return results;
}

function existsInPreviousResults(results: RouteGroup[], route: RunChangedParams) {
	return results.find(result => {
		return result.routes.some(currentRoute => currentRoute.runId === route.runId);
	});
}

function getAllPossibleRoutes(
	routesPlannerStore: RoutesPlannerStore,
	changedRoutes: RunChangedParams[]
): RunChangedParams[] {
	const routes: (RoutesPlannerRun | EtaResponseRouteItem)[] = Object.values(routesPlannerStore.routesById).map(
		route => routesPlannerStore.routeInfoByIndex[route.id] || route
	);
	const consecutiveRoutes: RoutesPlannerRun[] = Array.from(
		routesPlannerStore.consecutiveRoutesStore.routesById.values()
	);

	return routes
		.concat(consecutiveRoutes)
		.map(route => {
			const runId = (route as EtaResponseRouteItem).run_id || (route as RoutesPlannerRun).id;
			const changedRoute = changedRoutes.find(changed => changed.runId === runId);

			return (
				changedRoute ||
				etaRoutesConverter.routeToRunChangedParams(
					route,
					routesPlannerStore.consecutiveRoutesStore.routesTasksByRouteId[runId]?.sort(
						(task1, task2) => task1.priority - task2.priority
					) || routesPlannerStore.runToTasks[runId],
					routesPlannerStore.runToBreaks[runId]
				)
			);
		})
		.filter(route => route.runOrderedTasks?.length > 0)
		.sort((routeA, routeB) => routeA.runStartTime - routeB.runStartTime);
}

function getGroupData(
	changedRoute: RunChangedParams,
	routesWithConsecutive: RunChangedParams[],
	routesPlannerStore: RoutesPlannerStore
): { nextRoutesRoot: RouteNode; previousRoutesRoot: RouteNode; byRunId: { [runId: number]: RouteNode } } {
	const byRunId = {};

	const nextRoutesRoot = addNextLevelRoutes(changedRoute, routesWithConsecutive, routesPlannerStore, byRunId);
	const previousRoutesRoot = addPreviousLevelRoutes(changedRoute, routesWithConsecutive, routesPlannerStore, byRunId);
	return { nextRoutesRoot, previousRoutesRoot, byRunId };
}

function addPreviousLevelRoutes(
	currentRoute: RunChangedParams,
	allRoutes: RunChangedParams[],
	routesPlannerStore: RoutesPlannerStore,
	byRunId: { [runId: number]: RouteNode }
) {
	const allRoutesModified = allRoutes.slice().reverse();

	allRoutesModified.forEach(route => {
		route.runStartTime *= -1;
	});

	const root: RouteNode = { route: currentRoute, nextRoutes: {}, parent: undefined };
	buildRoutes(root, root, allRoutesModified, routesPlannerStore, byRunId);

	allRoutesModified.forEach(route => {
		route.runStartTime *= -1;
	});

	return root;
}

function addNextLevelRoutes(
	currentRoute: RunChangedParams,
	allRoutes: RunChangedParams[],
	routesPlannerStore: RoutesPlannerStore,
	byRunId: { [runId: number]: RouteNode }
) {
	const root: RouteNode = { route: currentRoute, nextRoutes: {}, parent: undefined };
	byRunId[currentRoute.runId] = root;
	buildRoutes(root, root, allRoutes, routesPlannerStore, byRunId);

	return root;
}

function buildRoutes(
	currentRoute: RouteNode,
	root: RouteNode,
	allRoutes: RunChangedParams[],
	routesPlannerStore: RoutesPlannerStore,
	byRunId: { [runId: number]: RouteNode }
) {
	const nextRouteByDriver = findByResource(currentRoute.route, allRoutes, 'driverId', byRunId);
	const nextRouteByVehicle =
		routesPlannerStore.optimizationType === OPTIMIZATION_TYPE.COMBINED &&
		findByResource(currentRoute.route, allRoutes, 'vehicleId', byRunId);

	addRoute(nextRouteByDriver, currentRoute, root, allRoutes, routesPlannerStore, byRunId);
	addRoute(nextRouteByVehicle, currentRoute, root, allRoutes, routesPlannerStore, byRunId);
}

function addRoute(
	nextRoute: RunChangedParams,
	currentRoute: RouteNode,
	root: RouteNode,
	allRoutes: RunChangedParams[],
	routesPlannerStore: RoutesPlannerStore,
	byRunId: { [runId: number]: RouteNode }
) {
	const existing = byRunId[nextRoute?.runId];

	if (!nextRoute || existing) {
		return;
	}

	let foundParent = undefined;

	traverseRoutesBfs(currentRoute, routeNode => {
		const matchByDriver = nextRoute.driverId && nextRoute.driverId === routeNode.route.driverId;
		const matchByVehicle =
			routesPlannerStore.optimizationType === OPTIMIZATION_TYPE.COMBINED &&
			nextRoute.vehicleId &&
			nextRoute.vehicleId === routeNode.route.vehicleId;

		const isAfterSelf =
			nextRoute.runStartTime <= routeNode.route.runStartTime && nextRoute.runId !== routeNode.route.runId;

		if ((matchByDriver || matchByVehicle) && isAfterSelf) {
			foundParent = routeNode.parent;
		}

		return isAfterSelf;
	});

	const nextRouteNode: RouteNode = { route: nextRoute, nextRoutes: {}, parent: undefined };
	byRunId[nextRoute.runId] = nextRouteNode;

	if (foundParent) {
		nextRouteNode.nextRoutes = foundParent.nextRoutes;
		foundParent.nextRoutes = { [nextRoute.runId]: nextRouteNode };
		nextRouteNode.parent = foundParent;

		for (const child of Object.values(nextRouteNode.nextRoutes)) {
			child.parent = nextRouteNode;
		}
	} else {
		currentRoute.nextRoutes[nextRoute.runId] = nextRouteNode;
		nextRouteNode.parent = currentRoute;
	}

	buildRoutes(nextRouteNode, root, allRoutes, routesPlannerStore, byRunId);
}

function traverseRoutesBfs(startingNode: RouteNode, callback: (routeNode: RouteNode) => boolean) {
	const queue: RouteNode[] = [startingNode];
	const visited = new Set<number>([startingNode.route.runId]);

	while (queue.length > 0) {
		const routeNode = queue.pop();

		const shouldStop = callback(routeNode);

		if (shouldStop) {
			break;
		}

		Object.values(routeNode.nextRoutes)
			.filter(nextRouteNode => !visited.has(nextRouteNode.route.runId))
			.forEach(nextRouteNode => {
				visited.add(nextRouteNode.route.runId);
				queue.unshift(nextRouteNode);
			});
	}
}

function findByResource(
	route: RunChangedParams,
	allRoutes: RunChangedParams[],
	resourceField: 'driverId' | 'vehicleId',
	byRunId: { [runId: number]: RouteNode }
) {
	return (
		route[resourceField] &&
		allRoutes.find(
			currentRoute =>
				currentRoute.runId !== route.runId &&
				currentRoute[resourceField] === route[resourceField] &&
				currentRoute.runStartTime >= route.runStartTime &&
				!byRunId[currentRoute.runId] &&
				dateUtils
					.getDateFromUnix(route.runStartTime)
					.isSame(dateUtils.getDateFromUnix(currentRoute.runStartTime), 'day')
		)
	);
}

export const etaRoutesProvider = { getGroupedByResource, getRouteNodes };
